import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams, Navigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { toast } from 'react-toastify';
import BaseLayout from '../../layouts/BaseLayout';
import DateFastFilter from '../AutomaticAssessmentPage/components/DateFastFilter';
import CustomPagination from '../AutomaticAssessmentPage/components/Pagination';
import PerPageDropDown from '../AutomaticAssessmentPage/components/PerPageDropDown';
import CustomDatePicker, { TDatePickerItem } from '../../components/CustomDatePicker';
import { IDateFilter } from '../AutomaticAssessmentPage';
import DateService from '../../services/DateService';
import CompanyObserver from '../../mobx/company/CompanyObserver';
import FetchService from '../../services/api/FetchService';
import {
    ICallChecklistAuditSimilarityResource,
} from '../../resources/ICallChecklistAuditSimilarity.resource';
import dayjs from 'dayjs';
import ModalFilterOperator, { IFilterSelect } from '../AutomaticAssessmentPage/components/ModalFilterOperator';
import FilterTag from '../AutomaticAssessmentPage/components/FilterTag';
import ClearAllTagsBtn from '../AutomaticAssessmentPage/components/ClearAllTagsBtn';
import InputSearch from '../AutomaticAssessmentPage/components/InputSearch';
import ModalDurationFilter from '../AutomaticAssessmentPage/components/ModalDurationFilter';
import ChecklistSwitch, { IChecklistSwitch } from '../ChecklistPage/components/ChecklistSwitch';
import Loader from '../../components/Loader';
import useQCompanies from '../../queries/useQCompanies';
import useQProfile from '../../queries/useQProfile';
import { EnumModules } from '../../resources/IProfile.resource';
import ROUTES from '../../routes';
import { EnumDateFastFilterType, initialDateFilters } from '../../constants/common';
import ChecklistAuditTable from './components/ChecklistAuditTable';
import LoadCallsFile from '../AutomaticAssessmentPage/components/LoadCallsFile';
import styles from './styles.module.scss';
import { ErrorToastSettings } from '../../services/toast/toastSettings';

export const enum EnumChecklistAuditTypes {
    perPage = 'perPage',
    page = 'page',
    phoneNumber = 'phoneNumber',
    startDate = 'startDate',
    endDate = 'endDate',
    dateFilterType = 'dateFilterType',
    operator = 'operator',
    duration = 'duration',
}

const safeJsonParse = <T,>(value: string | null): T | null => {
    if (!value) return null;
    try {
        return JSON.parse(value) as T;
    } catch {
        return null;
    }
};

const ChecklistAuditPage = () => {
    const { companies } = useQCompanies();
    const { profile } = useQProfile();
    const [total, setTotal] = useState<number>(0);
    const [searchParams, setSearchParams] = useSearchParams();
    const [auditData, setAuditData] = useState<ICallChecklistAuditSimilarityResource[]>([]);
    const [columnStats, setColumnStats] = useState<Record<string, number>>({});
    const [allChecklists, setAllChecklists] = useState<IChecklistSwitch[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isOpenModal, setIsOpenModal] = useState<EnumChecklistAuditTypes | null>(null);

    const tableRef = useRef<HTMLDivElement>(null);

    const companyId = CompanyObserver.companyId ?? CompanyObserver.getCompanyId();

    const selectedChecklistId = allChecklists.find((item) => item.isSelected)?.id ?? 0;

    const company = companies?.companies.find((item) => item.id === companyId);
    const hasAuditAccess = !companies || !profile ||
        (company?.modules.includes(EnumModules.PHONE_CALL_AUTO) === true &&
         profile.modules.includes(EnumModules.AUDIT));

    const handleCloseFilterModal = () => setIsOpenModal(null);

    const getSearchParams = (key: string) => searchParams.get(key);

    const handleSetPage = (page: number) => {
        setSearchParams((prev) => {
            prev.set('page', String(page));
            return prev;
        });
    };

    const handleSetPerPage = (perPage: number) => {
        setSearchParams((prev) => {
            prev.set('perPage', String(perPage));
            prev.set('page', '1');
            return prev;
        });
    };

    const handleSelectFastDateFilter = (start: string, end: string, type: EnumDateFastFilterType) => {
        setSearchParams((prev) => {
            prev.delete('phoneNumber');
            prev.set('startDate', start);
            prev.set('endDate', end);
            prev.set('dateFilterType', type);
            prev.set('page', '1');
            return prev;
        });
    };

    const getSearchParamsCustomRangeDate = (key: string): Date | null => {
        if (searchParams.get('dateFilterType') !== EnumDateFastFilterType.CUSTOM_RANGE) return null;
        const value = searchParams.get(key);
        if (!value || value === 'null') return null;
        return new Date(value);
    };

    const handleSelectDate = (date: [TDatePickerItem, TDatePickerItem]) => {
        setSearchParams((prev) => {
            prev.delete('phoneNumber');
            if (!date[0] && !date[1]) {
                prev.set('dateFilterType', EnumDateFastFilterType.TODAY);
                prev.set('startDate', DateService.formatUI(String(new Date()), 'YYYY-MM-DD'));
                prev.set('endDate', DateService.formatUI(String(new Date()), 'YYYY-MM-DD'));
                return prev;
            }
            prev.set('startDate', DateService.formatUI(String(date[0]), 'YYYY-MM-DD'));
            prev.set('endDate', date[1] ? DateService.formatUI(String(date[1]), 'YYYY-MM-DD') : 'null');
            prev.set('dateFilterType', EnumDateFastFilterType.CUSTOM_RANGE);
            return prev;
        });
    };

    const handleSetDuration = (from?: number, to?: number) => {
        setSearchParams((prev) => {
            if (from && to) {
                prev.set('duration', `${from}-${to}`);
            } else if (from) {
                prev.set('duration', `${from}-`);
            } else if (to) {
                prev.set('duration', `-${to}`);
            } else {
                prev.delete('duration');
            }
            return prev;
        });
    };

    const handleSetOperators = (filter: IFilterSelect) => {
        setSearchParams((prev) => {
            const parsed = safeJsonParse<IFilterSelect[]>(prev.get('operator'));
            if (parsed) {
                if (parsed.find((item) => item.value === filter.value)) {
                    const filtered = parsed.filter((item) => item.value !== filter.value);
                    if (filtered.length === 0) {
                        prev.delete('operator');
                        return prev;
                    }
                    prev.set('operator', JSON.stringify(filtered));
                    return prev;
                }
                parsed.push(filter);
                prev.set('operator', JSON.stringify(parsed));
                return prev;
            }
            prev.set('operator', JSON.stringify([filter]));
            return prev;
        });
    };

    const handleClearOperators = () => {
        setSearchParams((prev) => {
            prev.delete('operator');
            return prev;
        });
    };

    const handleSelectSort = (value: string) => {
        setSearchParams((prev) => {
            if (value === '_') {
                prev.delete('sort');
                return prev;
            }
            prev.set('sort', value);
            return prev;
        });
    };

    const getSortQuery = () => {
        const searchQuery = getSearchParams('sort');
        if (!searchQuery) return 'phoneCall.callTime desc';
        return `${searchQuery.split('_')[0]} ${searchQuery.split('_')[1]}`;
    };

    const paramsFactory = () => {
        const startDate = DateService.transformPicker(getSearchParams('startDate'));
        const endDate = getSearchParams('endDate');
        if (!startDate) return undefined;

        const perPage = getSearchParams(EnumChecklistAuditTypes.perPage);
        const page = getSearchParams(EnumChecklistAuditTypes.page);

        const parseFilters = () => {
            let filters = '';
            const duration = getSearchParams(EnumChecklistAuditTypes.duration);
            if (duration) {
                const [startDuration, endDuration] = duration.split('-');
                if (startDuration) filters += `phoneCall.duration ge ${startDuration} and `;
                if (endDuration) filters += `phoneCall.duration le ${endDuration} and `;
            }
            return filters;
        };

        const parseOperators = () => {
            const operators = safeJsonParse<IFilterSelect[]>(getSearchParams('operator'));
            if (!operators) return '';
            const operatorIds = operators.map((unit) => Number(unit.value));
            return operatorIds.length ? `phoneCall.employee.id in [${operatorIds}] and ` : '';
        };

        const parseSearch = () => {
            const value = getSearchParams('search');
            if (!value) return null;
            if (value.match(`^[0-9]*$`)) {
                return `phoneCall.phoneNumber cp '*${value}*'`;
            }
            return `phoneCall.employee.user.fullName lp '*${value.toLowerCase()}*'`;
        };

        const endDateMiddleware = () => {
            if (getSearchParams('startDate') === endDate) {
                return DateService.transformPicker(dayjs(startDate).add(2, 'day').format('YYYY-MM-DD'));
            }
            return !endDate || endDate === 'null'
                ? DateService.transformPicker(dayjs(startDate).add(1, 'days').format('YYYY-MM-DD'))
                : DateService.transformPicker(endDate);
        };

        const params = new URLSearchParams();
        params.set('pageSize', String(perPage));
        params.set('pageToken', String(Number(page) - 1));

        const search = parseSearch();
        if (search) {
            params.set('searchRequest', `${search} and ${parseFilters()}phoneCall.callTime ge 'T${startDate}' and phoneCall.callTime lt 'T${endDateMiddleware()}'`);
        } else {
            params.set('searchRequest', `${parseOperators()}${parseFilters()}phoneCall.callTime ge 'T${startDate}' and phoneCall.callTime lt 'T${endDateMiddleware()}'`);
        }
        params.set('orderByRequest', getSortQuery());
        return params;
    };

    const getTags = () => {
        const tags: { title: string; type: string }[] = [];
        const search = searchParams.get('search');
        const operators = safeJsonParse<IFilterSelect[]>(getSearchParams(EnumChecklistAuditTypes.operator));
        const duration = getSearchParams(EnumChecklistAuditTypes.duration);

        if (operators) {
            tags.push(...operators.map((item) => ({ title: item.label, type: `operator_${item.value}` })));
        }

        if (duration) {
            const [startDuration, endDuration] = duration.split('-');
            if (endDuration) {
                tags.push({
                    title: `duration: ${startDuration || '0'}sec-${endDuration}sec`,
                    type: EnumChecklistAuditTypes.duration,
                });
            } else {
                tags.push({
                    title: `duration: >${startDuration || '0'}sec`,
                    type: EnumChecklistAuditTypes.duration,
                });
            }
        }

        if (search) {
            tags.push({ title: search, type: 'search' });
        }

        return tags;
    };

    const handleClearTag = (type: string) => {
        setSearchParams((prev) => {
            if (type.split('_')[0] === 'operator') {
                const parsed = safeJsonParse<IFilterSelect[]>(prev.get('operator'));
                if (!parsed) return prev;
                const filtered = parsed.filter((item) => item.value !== type.split('_')[1]);
                if (filtered.length === 0) {
                    prev.delete('operator');
                    return prev;
                }
                prev.set('operator', JSON.stringify(filtered));
                return prev;
            }
            prev.delete(type);
            return prev;
        });
    };

    const handleClearAllTags = () => {
        setSearchParams((prev) => {
            prev.delete('phoneNumber');
            prev.delete('search');
            prev.delete('operator');
            prev.delete(EnumChecklistAuditTypes.duration);
            return prev;
        });
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchParams((prev) => {
            prev.delete('phoneNumber');
            prev.set('search', value);
            if (!value) prev.delete('search');
            return prev;
        });
    };

    const handleLoadCSV = async () => {
        if (!companyId || !selectedChecklistId) return;
        const params = paramsFactory();
        if (!params) return;
        params.delete('pageSize');
        params.delete('pageToken');

        try {
            setIsLoading(true);
            const res = await FetchService.getChecklistAuditSimilarityCsv(companyId, selectedChecklistId, params);
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `audit-report-${dayjs(new Date()).format('DD MMM YY')}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch {
            toast.error('Не вдалося завантажити CSV', ErrorToastSettings);
        } finally {
            setIsLoading(false);
        }
    };

    const loadAuditData = useCallback(async () => {
        if (!companyId || !selectedChecklistId) return;

        const params = paramsFactory();
        if (!params) return;

        try {
            setIsLoading(true);
            const [result, statsRes] = await Promise.all([
                FetchService.getChecklistAuditSimilarity(companyId, selectedChecklistId, params),
                FetchService.getChecklistAuditColumnStats(companyId, selectedChecklistId, params),
            ]);
            setAuditData(result.calls);
            setTotal(result.total);
            setColumnStats(statsRes);
        } catch {
            toast.error('Не вдалося завантажити дані аудиту', ErrorToastSettings);
        } finally {
            setIsLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyId, searchParams, selectedChecklistId]);

    const loadAllChecklists = useCallback(async () => {
        if (!companyId) return;

        try {
            setIsLoading(true);
            const res = await FetchService.getAuditChecklists(companyId);
            setAllChecklists(
                res.data.map((item, index) => ({
                    id: item.id,
                    name: item.name,
                    isSelected: index === 0,
                }))
            );
        } catch {
            toast.error('Не вдалося завантажити чеклісти', ErrorToastSettings);
        } finally {
            setIsLoading(false);
        }
    }, [companyId]);

    const renderModal = () => {
        switch (isOpenModal) {
            case EnumChecklistAuditTypes.operator:
                return (
                    <ModalFilterOperator
                        handleClose={handleCloseFilterModal}
                        handleSetOperator={handleSetOperators}
                        selectedOperatorsJSON={searchParams.get('operator')}
                        handleClearOperators={handleClearOperators}
                    />
                );
            case EnumChecklistAuditTypes.duration: {
                const [start, end] = searchParams.get('duration')?.split('-') ?? [];
                return (
                    <ModalDurationFilter
                        initFrom={start ?? ''}
                        initTo={end ?? ''}
                        handleClose={handleCloseFilterModal}
                        handleEditFilter={handleSetDuration}
                    />
                );
            }
            default:
                return null;
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (!getSearchParams('page') || !getSearchParams('perPage') || !getSearchParams('dateFilterType')) {
            setSearchParams((prev) => {
                prev.set('page', '1');
                prev.set('perPage', '50');
                prev.set('dateFilterType', EnumDateFastFilterType.LAST_30_DAYS);
                prev.set('startDate', DateService.getLast30Days().start);
                prev.set('endDate', DateService.getLast30Days().end);
                return prev;
            });
        }
    }, []);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (!getSearchParams('page') || !getSearchParams('perPage') || !getSearchParams('dateFilterType')) {
            setSearchParams((prev) => {
                prev.set('page', '1');
                prev.set('perPage', '50');
                prev.set('dateFilterType', EnumDateFastFilterType.LAST_30_DAYS);
                prev.set('startDate', DateService.getLast30Days().start);
                prev.set('endDate', DateService.getLast30Days().end);
                return prev;
            });
        }
    }, [searchParams]);

    useEffect(() => {
        loadAllChecklists();
    }, [loadAllChecklists]);

    useEffect(() => {
        loadAuditData();
    }, [loadAuditData]);

    if (!hasAuditAccess) {
        return <Navigate to={ROUTES.HOME} replace />;
    }

    const tags = getTags();

    return (
        <>
            {isLoading ? <Loader /> : null}
            {renderModal()}
            <BaseLayout title="Аудит схожості">
                <div className={styles.automatic__header}>
                    <div className={styles.automatic__dates}>
                        {initialDateFilters.map((item: IDateFilter) => {
                            if (item.type === EnumDateFastFilterType.CUSTOM_RANGE) return null;
                            return (
                                <DateFastFilter
                                    key={item.type}
                                    type={item.type}
                                    isActive={String(item.type) === getSearchParams('dateFilterType')}
                                    handleSetDate={handleSelectFastDateFilter}
                                />
                            );
                        })}
                        <CustomDatePicker
                            startDate={getSearchParamsCustomRangeDate('startDate')}
                            endDate={getSearchParamsCustomRangeDate('endDate')}
                            handleSelectDate={handleSelectDate}
                        />
                        <div className={styles.automatic__total}>
                            Загальна кількість: {total}
                        </div>
                    </div>
                    <div className={styles.automatic__header_left}>
                        <InputSearch
                            value={getSearchParams('search') ?? ''}
                            onChange={handleSearchChange}
                        />
                        <LoadCallsFile onClick={handleLoadCSV} isLoading={isLoading} />
                    </div>
                </div>
                {tags.length > 0 && (
                    <div className={styles.automatic__tags}>
                        {tags.map((item) => (
                            <FilterTag
                                key={item.title}
                                type={item.type}
                                title={item.title}
                                handleDelete={handleClearTag}
                            />
                        ))}
                        <ClearAllTagsBtn onClick={handleClearAllTags} />
                    </div>
                )}
                <div className={styles.automatic__subheader}>
                    <div />
                    <ChecklistSwitch setChecklists={setAllChecklists} checklists={allChecklists} />
                    <div />
                </div>
                <ChecklistAuditTable
                    ref={tableRef}
                    data={auditData}
                    selectedSort={getSearchParams('sort') ?? ''}
                    handleSelectSort={handleSelectSort}
                    filter={getSearchParams(EnumChecklistAuditTypes.operator) ?? undefined}
                    filterDuration={getSearchParams('duration') ?? undefined}
                    companyId={companyId ?? 0}
                    checklistId={selectedChecklistId}
                    columnStats={columnStats}
                />
                <div className={styles.automatic__footer}>
                    <div />
                    <CustomPagination
                        total={total}
                        perPage={Number(getSearchParams('perPage'))}
                        page={Number(getSearchParams('page'))}
                        setPage={handleSetPage}
                    />
                    <PerPageDropDown
                        setPerPage={handleSetPerPage}
                        perPage={Number(getSearchParams('perPage'))}
                    />
                </div>
            </BaseLayout>
        </>
    );
};

export default observer(ChecklistAuditPage);
